import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  useTheme
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
  BRAND_COLOR,
  BRAND_HOVER,
  combinePhoneNumber,
  getDefaultCountryCode,
  PhoneCountryCodeMap,
  useKivunovaTranslation,
  HandledGraphQLMutationError,
  translateSupplierCreateConflict
} from "@kivunova/kivufrontendcommon";
import { useCreateSupplier } from "../../hooks/api/useSupplier";
import type { CreateSupplierInput, Supplier } from "../../types/supplier";
import {
  buildQuickSupplierSchema,
  QuickSupplierFormValues
} from "./quickSupplierSchema";
import FormModal, { formFieldSx } from "../common/FormModal/FormModal";

const QUICK_ADD_SUPPLIER_FORM_ID = "quick-add-supplier-form";

export interface QuickAddSupplierModalProps {
  open: boolean;
  onClose: () => void;
  /** When true, `storeIdForCreate` must be set before submit (company admin + product store). */
  requireStoreId: boolean;
  storeIdForCreate?: string | null;
  onSupplierCreated: (supplier: Supplier) => void;
}

const QuickAddSupplierModal: React.FC<QuickAddSupplierModalProps> = ({
  open,
  onClose,
  requireStoreId,
  storeIdForCreate,
  onSupplierCreated
}) => {
  const { t } = useKivunovaTranslation();
  const theme = useTheme();
  const matchMD = useMediaQuery(theme.breakpoints.down("md"));
  const createSupplier = useCreateSupplier();
  const [submitApiError, setSubmitApiError] = useState<string | null>(null);

  const schema = useMemo(() => buildQuickSupplierSchema(t), [t]);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting }
  } = useForm<QuickSupplierFormValues>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: yupResolver(schema as any),
    mode: "onChange",
    defaultValues: {
      name: "",
      phone: "",
      phoneCountryCode: getDefaultCountryCode(),
      email: ""
    }
  });

  useEffect(() => {
    if (!open) return;
    setSubmitApiError(null);
    reset({
      name: "",
      phone: "",
      phoneCountryCode: getDefaultCountryCode(),
      email: ""
    });
  }, [open, reset]);

  const storeReady = !requireStoreId || Boolean(storeIdForCreate?.trim());

  const onSubmit: SubmitHandler<QuickSupplierFormValues> = async (data) => {
    if (!storeReady) return;

    const trimmedLocal = (data.phone ?? "").trim();
    const fullPhone = trimmedLocal
      ? combinePhoneNumber(
          data.phoneCountryCode as string,
          trimmedLocal,
          PhoneCountryCodeMap
        )
      : undefined;

    const input: CreateSupplierInput = {
      name: data.name.trim(),
      ...(fullPhone ? { phone: fullPhone } : {}),
      ...(data.email?.trim() ? { email: data.email.trim() } : {}),
      ...(requireStoreId && storeIdForCreate?.trim()
        ? { storeId: storeIdForCreate.trim() }
        : {})
    };

    try {
      const response = await createSupplier.mutateAsync(input);
      const created = response?.createSupplier?.supplier;
      if (created?.id) {
        onSupplierCreated({
          id: created.id,
          name: created.name,
          email: created.email ?? undefined,
          phone: created.phone ?? undefined,
          status: created.status ? String(created.status) : undefined
        });
        onClose();
      }
    } catch (error) {
      if (error instanceof HandledGraphQLMutationError && error.apiCode) {
        const msg = translateSupplierCreateConflict(t, error.apiCode);
        setSubmitApiError(
          msg ??
            t(
              "create-supplier-failed",
              "Could not create supplier. Please try again."
            )
        );
      }
    }
  };

  const busy = isSubmitting || createSupplier.isPending;

  const handleModalClose = () => {
    if (!busy) onClose();
  };

  return (
    <FormModal
      open={open}
      onClose={handleModalClose}
      title={t("quick-add-supplier-title", "New supplier")}
      maxWidth="sm"
      dataTestId="quick-add-supplier-modal"
      footer={
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            width: "100%",
            minWidth: 0,
            flex: "1 1 100%"
          }}
        >
          <LoadingButton
            form={QUICK_ADD_SUPPLIER_FORM_ID}
            type="submit"
            variant="contained"
            loading={busy}
            disabled={!storeReady}
            fullWidth
            size={matchMD ? "medium" : "large"}
            sx={{
              textTransform: "none",
              borderRadius: 2,
              fontWeight: 600,
              backgroundColor: BRAND_COLOR,
              boxShadow: "none",
              "&:hover": { backgroundColor: BRAND_HOVER, boxShadow: "none" }
            }}
          >
            {t("quick-add-supplier-save", "Create supplier")}
          </LoadingButton>
          <Button
            type="button"
            variant="outlined"
            onClick={handleModalClose}
            disabled={busy}
            fullWidth
            size={matchMD ? "medium" : "large"}
            sx={{
              textTransform: "none",
              borderRadius: 2,
              backgroundColor: "white",
              color: BRAND_COLOR,
              border: `1px solid ${BRAND_COLOR}`,
              "&:hover": {
                backgroundColor: BRAND_HOVER,
                color: "white",
                borderColor: BRAND_HOVER
              }
            }}
          >
            {t("cancel", "Cancel")}
          </Button>
        </Box>
      }
    >
      <Box
        component="form"
        id={QUICK_ADD_SUPPLIER_FORM_ID}
        onSubmit={handleSubmit(onSubmit)}
        noValidate
        sx={{
          ...formFieldSx,
          display: "flex",
          flexDirection: "column",
          gap: 2
        }}
      >
        {!storeReady && (
          <Typography variant="body2" color="error">
            {t(
              "quick-add-supplier-need-store",
              "Select a store for this product first."
            )}
          </Typography>
        )}

        {submitApiError ? (
          <Alert
            severity="error"
            onClose={() => setSubmitApiError(null)}
            sx={{ mb: 0.5 }}
          >
            {submitApiError}
          </Alert>
        ) : null}

        <Controller
          name="name"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              size={matchMD ? "small" : "medium"}
              label={t("name", "Name")}
              error={!!errors.name}
              helperText={errors.name?.message}
              disabled={busy}
            />
          )}
        />

        <Box
          sx={{
            display: "flex",
            gap: matchMD ? 1 : 2,
            flexDirection: matchMD ? "column" : "row",
            alignItems: "flex-start"
          }}
        >
          <Controller
            name="phoneCountryCode"
            control={control}
            render={({ field }) => (
              <FormControl
                variant="outlined"
                sx={{
                  flex: matchMD ? 1 : "0 0 160px",
                  minWidth: matchMD ? "100%" : 140
                }}
                size={matchMD ? "small" : "medium"}
                error={!!errors.phoneCountryCode}
                disabled={busy}
              >
                <InputLabel id="qa-supplier-phone-code-label">
                  {t("code", "Code")}
                </InputLabel>
                <Select
                  {...field}
                  labelId="qa-supplier-phone-code-label"
                  label={t("code", "Code")}
                  value={field.value || getDefaultCountryCode()}
                >
                  {Object.keys(PhoneCountryCodeMap).map((key) => (
                    <MenuItem key={key} value={key}>
                      {`${PhoneCountryCodeMap[key].flag} ${PhoneCountryCodeMap[key].code}`}
                    </MenuItem>
                  ))}
                </Select>
                {errors.phoneCountryCode && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ mt: 0.5, ml: 1.75 }}
                  >
                    {errors.phoneCountryCode.message}
                  </Typography>
                )}
              </FormControl>
            )}
          />
          <Controller
            name="phone"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                size={matchMD ? "small" : "medium"}
                label={t("phone-optional-label", "Phone (optional if email)")}
                error={!!errors.phone}
                helperText={errors.phone?.message}
                disabled={busy}
              />
            )}
          />
        </Box>

        <Controller
          name="email"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              size={matchMD ? "small" : "medium"}
              label={t("email-optional-label", "Email (optional if phone)")}
              error={!!errors.email}
              helperText={errors.email?.message}
              disabled={busy}
            />
          )}
        />
      </Box>
    </FormModal>
  );
};

export default QuickAddSupplierModal;
